import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { map, of, tap, catchError, mergeMap } from 'rxjs'
import { CommonService } from 'src/app/services/common/common.service'
import {
  forgot_password_failed,
  forgot_password_success,
  login_failed,
  login_success,
  logout_failed,
  logout_success,
  reset_password_with_password_failed,
  reset_password_with_password_success,
  select_client_failed,
  select_client_success,
  start_forgot_password,
  start_login,
  start_login_with_otp,
  start_logout,
  start_reset_password_with_password,
  start_select_client,
  start_sso_login
} from '../action/auth.action'
import { AppState } from '../state/app.state'
import { load_dashboard_action } from '../action/dashboard.action'
import { AuthService } from 'src/app/services/auth/auth.service'
import { TokenAPIModel } from 'src/app/models/api.model'
import { clear_initialize_data, start_initializing, update_password_auto_generated } from '../action/initialize.action'
import { SelectPeriodSelectorComponent } from 'src/app/components/common/select-period-selector/select-period-selector.component'

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private cs: CommonService,
    private appState: Store<AppState>
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_login),
      mergeMap(action =>
        this.authService.login(action.email, action.password).pipe(
          map((response: any) => {
            AuthService.setToken(response.token)
            AuthService.setRefreshToken(response.refreshToken)
            return login_success({ bearer_token: response.token })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error.message)

            this.cs.showError(errorMsg || 'INVALID EMAILID OR PASSWORD', 'Error', true)
            return of(login_failed(error))
          })
        )
      )
    )
  )

  login_with_otp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_login_with_otp),
      mergeMap(action =>
        this.authService.loginUsingOTP(action.email, action.otp).pipe(
          map((response: any) => {
            AuthService.setToken(response.token)
            AuthService.setRefreshToken(response.refreshToken)
            return login_success({ bearer_token: response.token })
          }),
          catchError((error: any) => {
            this.cs.showError('Invalid or expired OTP', 'Error', true)
            return of(login_failed(error))
          })
        )
      )
    )
  )

  loginRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(login_success),
        tap(action => {
          this.appState.dispatch(start_initializing({ comingFromLogin: true }))
        })
      )
    },
    { dispatch: false }
  )

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_logout),
      mergeMap(action =>
        this.authService.logout().pipe(
          map((response: any) => {
            return logout_success({ data: response })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message) || 'Unable to logout, please try again'
            this.cs.showError(errorMsg, 'LOGOUT FAILED', true)
            return of(logout_failed(error))
          })
        )
      )
    )
  )

  logoutRedirect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(logout_success),
        tap(action => {
          this.authService.clearLocalStorageAndRedirectToLogin()
          this.appState.dispatch(clear_initialize_data())
        })
      )
    },
    { dispatch: false }
  )

  select_client$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_select_client),
      mergeMap(({ groupId, entityIds, callback }) =>
        this.authService.callSelectClientAPI(groupId, entityIds).pipe(
          map((response: TokenAPIModel) => {
            return select_client_success({ callback, tokenModel: response })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message) || 'Unable to switch client, please try again'
            this.cs.showError(errorMsg, '', true)
            return of(select_client_failed(error))
          })
        )
      )
    )
  )

  select_client_success$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(select_client_success),
        tap(({ callback, tokenModel }) => {
          AuthService.setToken(tokenModel.token)
          this.appState.dispatch(load_dashboard_action({ from: null, to: null }))
          callback()
        })
      )
    },
    { dispatch: false }
  )

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_forgot_password),
      mergeMap(({ email, callback }) =>
        this.authService.callForgotPasswordAPI(email).pipe(
          map((response: any) => {
            return forgot_password_success({ callback })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message) || 'Unable to process your request, please try again'
            this.cs.showError(errorMsg, 'Something went wrong', true)
            return of(forgot_password_failed(error))
          })
        )
      )
    )
  )

  forgotPasswordSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(forgot_password_success),
        tap(({ callback }) => {
          this.cs.showSuccess('We will send you an email if we find your account.')
          callback()
        })
      )
    },
    { dispatch: false }
  )

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_reset_password_with_password),
      mergeMap(({ oldPassword, newPassword, callback }) =>
        this.authService.callResetPasswordWithOldPasswordAPI(oldPassword, newPassword).pipe(
          map((response: any) => {
            return reset_password_with_password_success({ callback })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message) || 'Unable to reset password, please try again'
            this.cs.showError(errorMsg, '', true)
            return of(reset_password_with_password_failed(error))
          })
        )
      )
    )
  )

  resetPasswordSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(reset_password_with_password_success),
        tap(({ callback }) => {
          this.cs.showSuccess('Password has been successfully changed')
          callback()
          this.appState.dispatch(update_password_auto_generated({ val: false }))
        })
      )
    },
    { dispatch: false }
  )

  sso_login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(start_sso_login),
      mergeMap(({ id_token, companyDomain }) =>
        this.authService.ssoLogin(id_token, companyDomain).pipe(
          map((response: any) => {
            AuthService.setToken(response.token)
            AuthService.setRefreshToken(response.refreshToken)
            return login_success({ bearer_token: response.token })
          }),
          catchError((error: any) => {
            const errorMsg = this.cs.extractApiErrorMessage(error?.message) || 'Unable to proceed at this time'
            this.cs.showError(errorMsg, 'Error', true)
            return of(login_failed(error))
          })
        )
      )
    )
  )
}
